import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ObservationStage from "./ObservationStage.jsx";
import DirectionStage from "./DirectionStage.jsx";
import BuildStage from "./BuildStage.jsx";
import DebriefStage from "./DebriefStage.jsx";
import "./OffTheClock.css";

const STAGES = ["intro", "observation", "direction", "build", "debrief"];
const STAGE_LABELS = { observation: "Observe", direction: "Direction", build: "Build", debrief: "Debrief" };

export default function OffTheClock() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("intro");
  const [observationResult, setObservationResult] = useState(null);
  const [plan, setPlan] = useState(null);
  const [buildResult, setBuildResult] = useState(null);

  const restart = () => {
    setObservationResult(null);
    setPlan(null);
    setBuildResult(null);
    setStage("intro");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="panel relative overflow-hidden p-6">
        <div className="mb-6">
          <p className="label-pixel text-gold">Level 07</p>
          <h1 className="pixel-heading text-base text-mist sm:text-xl">Off The Clock</h1>
        </div>

        {stage !== "intro" && (
          <div className="mb-6 flex gap-1.5">
            {["observation", "direction", "build", "debrief"].map((s) => {
              const idx = STAGES.indexOf(s);
              const currentIdx = STAGES.indexOf(stage);
              const complete = idx < currentIdx;
              const active = s === stage;
              return (
                <div key={s} className="flex-1">
                  <div
                    className={`h-1.5 w-full border border-black/60 ${
                      complete ? "bg-lime" : active ? "bg-cyan" : "bg-panel2"
                    }`}
                  />
                  <p
                    className={`mt-1 text-center font-display text-[7px] uppercase ${
                      active ? "text-cyan" : complete ? "text-lime" : "text-mist/30"
                    }`}
                  >
                    {STAGE_LABELS[s]}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="otc-panel-transition" key={stage}>
          {stage === "intro" && (
            <div className="text-center">
              <p className="mb-3 text-sm text-mist/80">
                Working hard pays the bills. It rarely builds freedom on its own — that takes
                leverage: something you build once that keeps paying off without you standing
                there the whole time. This level isn't about money you already have. It's about
                turning hours you don't think matter into something that does.
              </p>
              <p className="mb-6 text-xs text-mist/50">
                Three phases: notice real patterns worth solving, commit real hours to one of
                them, then hold the line through a compressed 90-day build.
              </p>
              <button onClick={() => setStage("observation")} className="btn-cyan">
                ▶ START OBSERVING
              </button>
            </div>
          )}

          {stage === "observation" && (
            <ObservationStage
              onComplete={(result) => {
                setObservationResult(result);
                setStage("direction");
              }}
            />
          )}

          {stage === "direction" && (
            <DirectionStage
              onDeploy={(deployedPlan) => {
                setPlan(deployedPlan);
                setStage("build");
              }}
            />
          )}

          {stage === "build" && plan && (
            <BuildStage
              onComplete={(result) => {
                setBuildResult(result);
                setStage("debrief");
              }}
            />
          )}

          {stage === "debrief" && observationResult && plan && buildResult && (
            <DebriefStage
              observationResult={observationResult}
              plan={plan}
              buildResult={buildResult}
              onRetry={restart}
            />
          )}
        </div>
      </div>
    </div>
  );
}
